import { Injectable } from '@nestjs/common';
import { randomUUID } from 'crypto';
import { AuditService } from './audit.service';
import { AuditContextService } from './audit-context.service';
import { capPayload, routePattern } from './audit-sampling';

export interface OutboundCallResult<T> {
  result: T;
  statusCode: number;
}

/**
 * Records calls we make OUT to third parties — Razorpay, the email provider,
 * object storage, pincode lookups.
 *
 * `interface_log.direction` was designed for exactly this and had never been
 * set to anything but its 'inbound' default, so these calls had no record at
 * all. They are the ones that fail in production and are hardest to reconstruct
 * afterwards, and they carry the same correlation id as the inbound request
 * that triggered them — which is what makes a correlation view worth opening.
 */
@Injectable()
export class OutboundLogService {
  constructor(
    private readonly auditService: AuditService,
    private readonly auditContext: AuditContextService
  ) {}

  /**
   * Wraps an outbound call. Always records — outbound volume is a rounding
   * error next to inbound, and every one of these is worth keeping.
   */
  async track<T>(
    provider: string,
    method: string,
    url: string,
    call: () => Promise<T>,
    options?: { requestPayload?: unknown; statusOf?: (result: T) => number }
  ): Promise<T> {
    const startedAt = Date.now();
    const requestedOn = new Date(startedAt);

    try {
      const result = await call();

      this.write({
        provider,
        method,
        url,
        statusCode: options?.statusOf?.(result) ?? 200,
        requestPayload: options?.requestPayload,
        startedAt,
        requestedOn,
      });

      return result;
    } catch (error) {
      this.write({
        provider,
        method,
        url,
        statusCode: this.statusOf(error),
        requestPayload: options?.requestPayload,
        errorPayload: this.errorPayload(error),
        startedAt,
        requestedOn,
      });

      throw error;
    }
  }

  private write(input: {
    provider: string;
    method: string;
    url: string;
    statusCode: number;
    requestPayload?: unknown;
    errorPayload?: object;
    startedAt: number;
    requestedOn: Date;
  }): void {
    const capped = capPayload(this.auditService.maskPayload(input.requestPayload));

    this.auditService.logInterface({
      correlationId: this.auditContext.correlationId() ?? randomUUID(),
      batchId: this.auditContext.batchId(),
      direction: 'outbound',
      method: input.method.toUpperCase(),
      path: this.safePath(input.url),
      routePattern: `${input.provider}:${routePattern(this.safePath(input.url))}`,
      statusCode: input.statusCode,
      requestPayload: capped.value,
      errorPayload: input.errorPayload,
      truncated: capped.truncated,
      sampleRate: 1,
      durationMs: Date.now() - input.startedAt,
      requestedOn: input.requestedOn,
      respondedOn: new Date(),
    });
  }

  /** Query strings on provider URLs routinely carry keys and signatures. */
  private safePath(url: string): string {
    try {
      const parsed = new URL(url);
      return `${parsed.origin}${parsed.pathname}`;
    } catch {
      return url.split('?')[0];
    }
  }

  private statusOf(error: unknown): number {
    if (typeof error === 'object' && error) {
      const candidate = error as { status?: unknown; statusCode?: unknown; response?: { status?: unknown } };
      const status = candidate.status ?? candidate.statusCode ?? candidate.response?.status;
      if (typeof status === 'number') return status;
    }

    return 0; // no HTTP response at all — DNS failure, timeout, refused
  }

  private errorPayload(error: unknown): object {
    return error instanceof Error
      ? { name: error.name, message: error.message }
      : { message: String(error) };
  }
}
